import {
  ErrorI,
  isEmail,
  isPhone
} from "get-life-storybook-ts/lib/components/Validations";
import { useEffect, useState } from "react";
import { useDevice } from "get-life-storybook-ts/lib/components/Hooks";
import { pushToDataLayer } from "../../helpers/gtm";
import { sha256 } from "../../helpers/String/sha256";

interface DataTrackerI {
  leadUlid?: string;
  campaign: string;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  tid?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_test?: string | null;
  gclid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  entryPage?: string | null;
  referrerUrl?: string | null;
  ABTestVersion: string | null;
}

interface FsEventI {
  FsEvent: string;
  data?: {
    leadId?: string;
  };
}

interface IdentifyFSI {
  lead: string;
  data: {
    email: string;
    emailDomain: string;
    brokerId: string;
  };
}
interface ContextProps {
  handleTrack: (value: HandleTrackerI) => void;
  identifyFS: (value: IdentifyFSI) => void;
}

interface HandleTrackerI {
  type: "FS" | "tracker" | "GA";
  data: any;
  event?: string;
}

export interface StrapiDataFormWealthReader {
  data: {
    Url: string;
    backgroundColor?: string;
    HasPhoneValidation?: boolean;
    HasSeriousIllnesses?: boolean;
  }[];
}
interface Validations extends ErrorI<string | number> { }

interface GlobalInfoI {
  defaultcap: string | null;
  gtf_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_test?: string | null;
  gclid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  tid?: string | null;
  url: any;
  entryPage?: string | null;
  referrerUrl?: string | null;
  ABTestVersion: string | null;
  ucc?: string | null
}
interface FormDataFirstI {
  birthDate: string;
  capital: number | string;
  haveInsurance: string;
}

interface FormDataI {
  phone: string;
  email: string;
  name: string;
}

const API_URL = process.env.GATSBY_API_URL;

const errorMessages: any = {
  birthDate: "La edad introducida no es asegurable",
  haveInsurance: "Por favor seleccione una opción",
  capital: "Por favor seleccione un capital",
  phone: "Por favor inserte un número de teléfono correcto",
  email: "Por favor inserte un correo electrónico correcto",
  checkedConditions: "Debe aceptar el tratamiento de datos para continuar",
  checkedConditions2: "Debe aceptar las condiciones para continuar",
};

const FormWealthReaderController = ({ data }: StrapiDataFormWealthReader) => {
  const [formData, setFormData] = useState<FormDataI>({
    phone: "",
    email: "",
    name: ""
  });
  const [formError, setFormError] = useState<any>();
  const [actualStep, setActualStep] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);

  const fieldValidation = ({ data, key, newError }: any) => {
    if (!data[key]) {
      newError = {
        ...newError,
        [key]: { error: true, message: errorMessages[key] },
      };
    } else if (key === "phone" && isPhone({ value: data[key] }).error) {
      newError = {
        ...newError,
        [key]: { error: true, message: errorMessages[key] },
      };
    } else if (key === "email" && isEmail({ value: data[key] }).error) {
      newError = {
        ...newError,
        [key]: { error: true, message: errorMessages[key] },
      };
    } else {
      newError = { ...newError, [key]: { error: false, message: "" } };
    }

    return newError;
  };

  const validations = (data: any) => {
    let newError: any = {};
    Object.keys(data).forEach((key: any) => {
      newError = fieldValidation({ data, key, newError });
    });
    let sumValidate = 0;
    Object.keys(newError).forEach((key) => {
      if (!newError[key].error) {
        sumValidate += 1;
      }
    });
    return sumValidate === Object.keys(newError).length ? undefined : newError;
  };

  const handleChangeDataLayer = async (newData: any) => {
    // Was `TagManager.dataLayer({ dataLayer: newData })`: react-gtm-module is a
    // thin wrapper that does exactly this push when window.dataLayer exists,
    // which it always does (seeded inline in <head> by gatsby-ssr.js).
    pushToDataLayer(newData);
  };

  const signupOrganic = async () => {
    let data = {
      email: formData.email,
      phone: formData.phone,
      name: formData.name
    };

    const organic = await fetch(
      `${API_URL}/public/hubspot/wealth-reader`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
      .then(async (response) => {
        const [hashedEmail, hashedPhone] = await Promise.all([
          sha256(formData.email),
          sha256(formData.phone),
        ]);

        const dataInfo = {
          event: "leadCreated",
          page: window.location.href,
          pageTitle: `Wealth Reader - Form complete`,
          email: hashedEmail,
          phone: hashedPhone,
          name: formData.name
        };

        handleChangeDataLayer(dataInfo);
        return response.json();
      })
      .then((resultData) => resultData);

    setActualStep(2);

    return organic;
  };

  const fetchOrganic = async (dataSecond: any) => {
    setLoading(true);
    try {
      const secondStepErrors = validations(dataSecond);
      if (secondStepErrors) {
        throw secondStepErrors;
      }

      await signupOrganic();

      setLoading(false);
    } catch (error) {
      setFormError(error);
      setLoading(false);
    }
  };

  const handleButton = async () => {
    await ValidateStepTwoAndGoToNextStep();
  };

  const ValidateStepTwoAndGoToNextStep = async () => {
    let dataSecond = formData;
    if (formData.email === "") {
      let newEmail = (document.getElementById("emailInput") as HTMLInputElement)
        .value;
      setFormData({ ...formData, email: newEmail });
      dataSecond = { ...formData, email: newEmail };
    }
    const secondErrors = validations(dataSecond);
    if (secondErrors) return setFormError(secondErrors);

    await fetchOrganic(dataSecond);
  };

  const handleChange = ({ setValue, value, field, formData }: any) => {
    let newData = { ...formData, [field]: value };
    let newError: any = {};
    if (value && formError && formError[field] && formError[field].error) {
      newError = fieldValidation({ data: newData, key: field, newError });
      setFormError({ ...formError, ...newError });
    }
    setValue(newData);
  };

  const handleBlur = ({ field, formData }: any) => {
    let newError: any = {};
    if (!!formData[field]) {
      newError = fieldValidation({ data: formData, key: field, newError });
      setFormError({ ...formError, ...newError });
    }
  };

  return {
    actualStep,
    formData,
    formError,
    handleBlur,
    handleButton,
    handleChange,
    loading,
    setActualStep,
    setFormData,
    validations,
  };
};

export default FormWealthReaderController;
